import { dNotify } from 'packages/lib/common-ui/notification/deduped-notify';
import {
  getDefaultHeaders,
  getBaseURLForAPIBackend,
  APIBackend,
} from 'packages/lib/utils/fetch';
import { preparePatchArray } from 'packages/lib/utils/api';
import { ZodType, ZodTypeDef, z } from 'zod';
import { get, set } from 'idb-keyval';
import {
  parsePCDBuffer,
  PCDContent,
} from 'packages/lidar-tool/utils/pcd-loader/parse-pcd';
import { ResponseNotOk } from 'packages/lib/utils/error';
import { ImageAnnotation } from 'packages/lib/api/annotations/contract/image-annotation';
import { checkCondition } from 'packages/lib/utils/check-condition';
import { AnyObject } from 'packages/lib/common-types/types';
import { logError } from 'packages/lib/utils/logger';
import { taskCountRespZod } from 'packages/lib/api/annotations/contract/task';

// Jun-11-2026 Used Cursor Auto to extend apiGetCached with showErrorToast option
/**
 * !IMPORTANT: Please await this function to guarantee error bubbling.
 */
export const okOrThrow = async (
  resp: Response,
  showErrorToast = true,
  handleRejection?: (resp: Response) => Promise<Response>
): Promise<Response> => {
  if (!resp.ok) {
    try {
      // try to log API error message
      const errorResponse = await resp?.json?.();
      if (errorResponse) {
        if (showErrorToast) {
          dNotify.error({
            message: 'Request Failed:',
            description: errorResponse['error'],
          });
        }
        logError(
          `non-200 API Response (code ${resp.status}) for ${
            resp.url
          }: ${JSON.stringify(errorResponse['error'])}`,
          true,
          handleRejection ? true : false
        );
      }
    } catch (_) {
      // this only catches if the error response doesn't have a JSON body
    }
    if (handleRejection) {
      return handleRejection(resp);
    }
    throw new ResponseNotOk(resp);
  }
  return resp;
};

/**
 * Core fetcher for ML Repo.
 * This should contain logic that should be applied to any/every outgoing fetch.
 */
export const apiFetch =
  (api: APIBackend, commonHeaders?: HeadersInit) =>
  async (
    endpoint: string,
    method?: string,
    body?: BodyInit,
    headers?: HeadersInit,
    showErrorToast?: boolean,
    handleRejection?: (resp: Response) => Promise<Response>
  ): Promise<Response> => {
    return fetch(`${getBaseURLForAPIBackend(api)}${endpoint}`, {
      method,
      body,
      headers: {
        ...(await getDefaultHeaders()).headers,
        ...(!commonHeaders ? undefined : commonHeaders),
        ...(!headers ? undefined : headers),
      },
    }).then((resp) => okOrThrow(resp, showErrorToast, handleRejection));
  };

export async function fetchAllPages<T>(
  baseUrl: string,
  zodParse: (json: unknown) => T[],
  pageSize = 2000
): Promise<T[]> {
  const [path, query] = baseUrl.split('?');
  const countUrl = query ? `${path}/count?${query}` : `${path}/count`;
  const countResp = await apiGet(countUrl, taskCountRespZod.parse);
  const total = countResp['*'];

  if (typeof total !== 'number' || total === 0) {
    return [];
  }

  const numPages = Math.ceil(total / pageSize);
  const pageFetches: Promise<T[]>[] = [];

  for (let pageNo = 1; pageNo <= numPages; pageNo++) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    const pageUrl = `${baseUrl}${separator}pageSize=${pageSize}&pageNo=${pageNo}`;
    pageFetches.push(apiGet<T[]>(pageUrl, (json: unknown) => zodParse(json)));
  }

  const pages = await Promise.all(pageFetches);
  return pages.flat();
}

/**
 * Annotations API Fetcher
 */
export const annotationsFetch = apiFetch('annotations', { 'If-Match': '*' });
const annotationsDirectFetch = apiFetch('annotations', {
  'If-Match': '*',
  'X-Direct-Access': 'yes',
});
const fetchDirectPCD = async (url: string): Promise<ArrayBuffer> => {
  const s3Response = await annotationsDirectFetch(url);
  const { location } = await s3Response.json();
  const pcdContent = await fetch(location);
  return pcdContent.arrayBuffer();
};

export const apiGet = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse: ZodType<T, Z>['parse']
): Promise<T> => {
  const resp = await annotationsFetch(endpoint);
  const json = await resp.json();

  try {
    return zodParse(json);
  } catch (e) {
    // Make it easier to debug Zod parse errors
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', json);
    throw e;
  }
};

export const apiGetWithEtag = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse: ZodType<T, Z>['parse']
): Promise<{ data: T; etag: string | undefined }> => {
  const response = await annotationsFetch(endpoint);
  const data = zodParse(await response.json());
  const etag = (response.headers.get('ETag') || undefined)?.replace(/^W\//, '');
  return { data, etag };
};

/** key: imageDataToken, value: array of all 2D ImageAnnotations for that image */
let preloadedImageAnnotations = new Map<string, ImageAnnotation[]>();
let imageAnnotationsHaveLoaded = false;
export function setPreloadedImageAnnotations(
  map: Map<string, ImageAnnotation[]>
): void {
  preloadedImageAnnotations = map;
  imageAnnotationsHaveLoaded = true;
}

/**  Image Annotations are static assets. They're not created, edited, or deleted by RAIL, and as such, can be preloaded startup.
 *  * @token - the image data token
 */
export const apiGetPreloadedImageAnnotation = async (
  imageDataToken: string
): Promise<ImageAnnotation[]> => {
  await checkCondition(() => imageAnnotationsHaveLoaded);

  if (preloadedImageAnnotations.has(imageDataToken)) {
    return preloadedImageAnnotations.get(imageDataToken) as ImageAnnotation[];
  }

  return [];
};

export const apiGetBlob = async (endpoint: string): Promise<Blob> => {
  return annotationsFetch(endpoint).then((r) => r.blob());
};

export const apiGetMany = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoints: string[],
  zodParse: ZodType<T, Z>['parse']
): Promise<T[]> => {
  const respArr = await Promise.all(
    (
      await Promise.all(endpoints.map((endpoint) => annotationsFetch(endpoint)))
    ).map(async (resp) => await resp.json())
  );
  return respArr.map((r) => zodParse(r));
};

/**
 * !important For use with immutable/idempotent assets ONLY
 */
export interface CachedFetchOptions {
  showErrorToast?: boolean;
}

export const apiGetCached = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse: ZodType<T, Z>['parse'],
  options: CachedFetchOptions = {}
): Promise<T> => {
  const { showErrorToast = true } = options;
  const forceCacheBust = window.location.search.includes('cache=none');
  const cachedData: T | null | undefined = forceCacheBust
    ? null
    : await get(endpoint).catch((err) => {
        console.warn(err);
        return null;
      });
  const data = cachedData
    ? cachedData
    : zodParse(
        await annotationsFetch(
          endpoint,
          undefined,
          undefined,
          undefined,
          showErrorToast
        ).then((r) => r.json())
      );
  if (!cachedData) {
    console.log(endpoint + ' initial cache.');
    void set(endpoint, data).catch(console.warn);
  } else {
    // console.log(endpoint + ' cache restored.', cachedData);
  }
  return data;
};

/**
 * !important For use with immutable/idempotent assets ONLY
 */
export const apiGetManyCached = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoints: string[],
  zodParse: ZodType<T, Z>['parse']
): Promise<T[]> => {
  const dataArr = await Promise.all(
    endpoints.map((endpoint) => apiGetCached(endpoint, zodParse))
  );
  return dataArr;
};

/**
 * !important For use with immutable/idempotent assets ONLY
 */
export const apiGetBlobCached = async (
  endpoint: string,
  options: CachedFetchOptions = {}
): Promise<Blob> => {
  const { showErrorToast = true } = options;
  const forceCacheBust = window.location.search.includes('cache=none');
  const cachedData: Blob | undefined = forceCacheBust
    ? undefined
    : await get(endpoint).catch((err) => {
        console.warn(err);
        return undefined;
      });
  const data = cachedData
    ? cachedData
    : await annotationsFetch(
        endpoint,
        undefined,
        undefined,
        undefined,
        showErrorToast
      ).then((r) => r.blob());

  if (!cachedData) {
    console.log(endpoint + ' initial cache.');
    void set(endpoint, data).catch(console.warn);
  }
  return data;
};

export type CachedPCDContent = PCDContent & { isCached: boolean };

/** Fetches parsed PCD from cache, or fetches from API, parses, and stores result in cache. */
export const fetchPCDCached = async (
  url: string
): Promise<CachedPCDContent> => {
  const forceCacheBust = window.location.search.includes('cache=none');
  const cachedPCD: PCDContent | null | undefined = forceCacheBust
    ? null
    : await get(url).catch((err) => {
        console.warn(err);
        return null;
      });
  const data = cachedPCD
    ? cachedPCD
    : parsePCDBuffer(await fetchDirectPCD(url));
  if (!cachedPCD) {
    // console.log(url + ' PCD initial cache.');
    void set(url, data).catch(console.warn);
  } else {
    // console.log(url + ' cache restored.', cachedPCD);
  }
  return { ...data, isCached: !!cachedPCD };
};

const validPOSTResponseZod = z.object({ token: z.string() });
type ValidPOSTResponse = z.infer<typeof validPOSTResponseZod>;
/**
 * FOR ANNOTATIONS API ENDPOINTS ONLY!!
 *  Assumes a post response returns an object that extends { token: string; }.
 *  Returns the given token.
 */
export const post = async <T>(
  endpoint: string,
  postData: T
): Promise<ValidPOSTResponse> =>
  validPOSTResponseZod.parse(
    await annotationsFetch(endpoint, 'POST', JSON.stringify(postData)).then(
      (r) => r.json()
    )
  );

export const put = async <T>(endpoint: string, postData: T): Promise<void> => {
  await annotationsFetch(endpoint, 'PUT', JSON.stringify(postData));
  return;
};

/**
 * FOR ANNOTATIONS API ENDPOINTS ONLY!!
 *  Assumes an empty post response.
 */
export const postEmpty = async <T>(
  endpoint: string,
  postData: T
): Promise<Response> =>
  annotationsFetch(endpoint, 'POST', JSON.stringify(postData));

/**
 * FOR ANNOTATIONS API ENDPOINTS ONLY!!
 * Assumes PATCH endpoints returns 204 No Content.
 */
export const patch = async <T extends AnyObject>(
  endpoint: string,
  patchData: T,
  headers?: HeadersInit
): Promise<Response> =>
  annotationsFetch(
    endpoint,
    'PATCH',
    JSON.stringify(preparePatchArray(patchData)),
    headers
  );

/**
 * Assumes PATCH endpoints returns 204 No Content.
 */
export const patchBulk = async <T extends AnyObject>(
  endpoint: string,
  patchData: Record<string, T>
): Promise<Response> => {
  const patchUpdate: Record<string, unknown> = {};
  for (const data in patchData) {
    patchUpdate[data] = preparePatchArray(patchData[data]);
  }
  return annotationsFetch(endpoint, 'PATCH', JSON.stringify(patchUpdate));
};

/**
 * Assumes DELETE endpoints returns 200 OK on success by default, change by providing the expected HTTP status as a second parameter.
 */
export const remove = (
  endpoint: string,
  successStatus = 200
): Promise<boolean> =>
  annotationsFetch(endpoint, 'DELETE').then((r) => r.status === successStatus);

/**
 * Assumes DELETE endpoints returns 200 OK on success by default, change by providing the expected HTTP status as a second parameter.
 */
export const removeBulk = (
  endpoint: string,
  data: string[],
  successStatus = 200
): Promise<boolean> =>
  annotationsFetch(endpoint, 'DELETE', JSON.stringify(data)).then(
    (r) => r.status === successStatus
  );

export const annotationsApi = {
  get: apiGet,
};
